import {
  Button,
  Checkbox,
  DatatableHead,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@monksflow/monks-ui';
import { ArrowDown, ArrowUp, ArrowUpDown, FilterIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CATEGORIES, type Category } from '@inference.engine/guidelines/web';
import { formatCategoryLabel } from '@/features/guidelines/constants';
import { CategoryBadgeCell } from '../CategoryBadgeCell';

const CATEGORY_NAMES = Object.keys(CATEGORIES) as Category[];

export interface CategoryFilterHeadProps {
  label: string;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: 'category') => void;
  selectedCategories: Category[];
  onCategoryFilterChange: (categories: Category[]) => void;
}

/**
 * The Category column head composes a sort trigger and a filter trigger side
 * by side. The sort click lives on an inner `<button>`, never on the `<th>`
 * itself, so the filter popover trigger can sit beside it without also
 * toggling the sort; the filter trigger stops propagation as a second guard.
 */
export function CategoryFilterHead({
  label,
  sortBy,
  sortDirection,
  onSort,
  selectedCategories,
  onCategoryFilterChange,
}: CategoryFilterHeadProps) {
  const { t } = useTranslation('knowledge');

  const isActiveSort = sortBy === 'category';
  // eslint-disable-next-line i18next/no-literal-string -- aria attribute values, not copy
  const ariaSort = isActiveSort ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';
  const hasActiveFilters = selectedCategories.length > 0;

  const toggleCategory = (category: Category) => {
    onCategoryFilterChange(
      selectedCategories.includes(category)
        ? selectedCategories.filter((c) => c !== category)
        : [...selectedCategories, category],
    );
  };

  return (
    <DatatableHead aria-sort={ariaSort} className="w-[15%]">
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="flex cursor-pointer items-center gap-1 select-none"
          onClick={() => onSort('category')}
        >
          {label}
          {isActiveSort ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="size-3.5" />
            ) : (
              <ArrowDown className="size-3.5" />
            )
          ) : (
            <ArrowUpDown className="size-3.5 text-muted-foreground" />
          )}
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-mini"
              aria-label={t('guidelines.categoryFilterHead.filterByCategory')}
              aria-pressed={hasActiveFilters}
              onClick={(event) => event.stopPropagation()}
            >
              <FilterIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex w-60 flex-col gap-2xs p-2xs">
            <div className="flex items-center justify-between gap-xs px-2xs py-2xs">
              <span className="text-label-sm text-muted-foreground">
                {t('guidelines.categoryFilterMenu.category')}
              </span>
              <Button variant="ghost" size="sm" onClick={() => onCategoryFilterChange([])}>
                {t('guidelines.categoryFilterMenu.clear')}
              </Button>
            </div>
            {CATEGORY_NAMES.map((category) => (
              <div
                key={category}
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center justify-between gap-xs rounded-sm px-2xs py-2xs hover:bg-accent"
                onClick={() => toggleCategory(category)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') toggleCategory(category);
                }}
              >
                <CategoryBadgeCell category={category} />
                <Checkbox
                  aria-label={formatCategoryLabel(category)}
                  checked={selectedCategories.includes(category)}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={() => toggleCategory(category)}
                />
              </div>
            ))}
          </PopoverContent>
        </Popover>
      </div>
    </DatatableHead>
  );
}

CategoryFilterHead.displayName = 'CategoryFilterHead';
